// src/client/api.ts — thin fetch wrapper for the BuddhaTomato API.
// Exposes window.ZenAPI.{get, post, put, delete, upload}.
// Auto-includes credentials, throws Error with .status/.data on non-2xx.

export class ZenApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ZenApiError";
    this.status = status;
    this.data = data;
  }
}

type Json = unknown;
let csrfToken: string | null = null;

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  return document.cookie.split(/;\s*/).find((p) => p.startsWith(prefix))?.slice(prefix.length) ?? null;
}

async function parse(r: Response): Promise<Json> {
  if (r.status === 204) return null;
  const text = await r.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function call<T = Json>(method: string, path: string, body?: Json): Promise<T> {
  const upper = method.toUpperCase();
  const init: RequestInit = {
    method: upper,
    credentials: "include",
    headers: {} as Record<string, string>,
  };
  if (!["GET", "HEAD", "OPTIONS"].includes(upper)) {
    csrfToken = csrfToken ?? readCookie("bt_csrf");
    if (!csrfToken) {
      const csrfRes = await fetch("/api/csrf", { credentials: "include" });
      const csrfData = await parse(csrfRes) as { token?: string } | null;
      csrfToken = csrfData?.token ?? readCookie("bt_csrf");
    }
    if (csrfToken) (init.headers as Record<string, string>)["x-bt-csrf"] = csrfToken;
  }
  if (body !== undefined) {
    (init.headers as Record<string, string>)["content-type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const r = await fetch(path, init);
  const data = await parse(r);
  if (!r.ok) {
    const msg =
      data && typeof data === "object" && "error" in (data as Record<string, unknown>)
        ? String((data as { error: unknown }).error)
        : r.statusText || `HTTP ${r.status}`;
    throw new ZenApiError(msg, r.status, data);
  }
  return data as T;
}

async function upload<T = Json>(path: string, formData: FormData): Promise<T> {
  csrfToken = csrfToken ?? readCookie("bt_csrf");
  if (!csrfToken) {
    const csrfRes = await fetch("/api/csrf", { credentials: "include" });
    const csrfData = await parse(csrfRes) as { token?: string } | null;
    csrfToken = csrfData?.token ?? readCookie("bt_csrf");
  }
  const r = await fetch(path, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: csrfToken ? { "x-bt-csrf": csrfToken } : undefined,
  });
  const data = await parse(r);
  if (!r.ok) {
    const msg =
      data && typeof data === "object" && "error" in (data as Record<string, unknown>)
        ? String((data as { error: unknown }).error)
        : r.statusText || `HTTP ${r.status}`;
    throw new ZenApiError(msg, r.status, data);
  }
  return data as T;
}

export interface ZenAPI {
  get: <T = Json>(path: string) => Promise<T>;
  post: <T = Json>(path: string, body?: Json) => Promise<T>;
  put: <T = Json>(path: string, body?: Json) => Promise<T>;
  delete: <T = Json>(path: string) => Promise<T>;
  upload: <T = Json>(path: string, formData: FormData) => Promise<T>;
}

export const ZenAPI: ZenAPI = {
  get: (p) => call("GET", p),
  post: (p, b) => call("POST", p, b === undefined ? {} : b),
  put: (p, b) => call("PUT", p, b === undefined ? {} : b),
  delete: (p) => call("DELETE", p),
  upload,
};

// Back-compat for any inline HTML still using window.ZenAPI.
window.ZenAPI = ZenAPI;
